import React, { useContext, useEffect, useRef, useState } from "react";
import { AuthContext } from "../../context/AuthContext.jsx";
import { getReconciliation } from "../../services/reconciliationServices.js";
import { createLatestRequestGate } from "../../utils/latestRequest.js";
import { RECONCILIATION_CATEGORIES, RECONCILIATION_PAGE_LIMIT, parseReconciliationResponse, formatDiagnosticDate } from "../../utils/reconciliationPresentation.js";
import { RESULT_CONFIG } from "../../utils/configStatus.js";
import { resultCode } from "../../utils/resultPresentation.js";

export default function ReconciliationPanel() {
  const auth = useContext(AuthContext);
  const isAdmin = auth?.user?.role === "admin";
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("held");
  const [page, setPage] = useState(1);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const gate = useRef(createLatestRequestGate());
  useEffect(() => {
    if (!open || !isAdmin) return;
    const request = gate.current.begin();
    setLoading(true); setData(null); setError("");
    getReconciliation({ category, page, limit: RECONCILIATION_PAGE_LIMIT }).then((response) => {
      const parsed = parseReconciliationResponse(response, category, page);
      if (request.isCurrent()) setData(parsed);
    }).catch((failure) => {
      if (request.isCurrent()) setError(failure?.response?.status === 403
        ? "Ce diagnostic nécessite un accès administrateur."
        : "Le diagnostic n’a pas pu être chargé. Réessayez après vérification.");
    }).finally(() => { if (request.isCurrent()) setLoading(false); });
    return () => gate.current.invalidate();
  }, [open, isAdmin, category, page, refreshVersion]);
  if (!isAdmin) return null;
  return <section className="card my-3" aria-label="Diagnostic des rappels">
    <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
      <h2 className="h6 m-0">Diagnostic des rappels</h2>
      <button type="button" className="btn btn-outline-secondary btn-sm" aria-expanded={open}
        onClick={() => { setOpen(!open); if (open) setData(null); }}>
        {open ? "Fermer le diagnostic" : "Consulter le diagnostic"}
      </button>
    </div>
    {open && <div className="card-body">
      <p className="text-muted">Lecture seule. Une réservation peut correspondre à un appel en cours.
        Vérifiez les appels réels avant toute intervention.</p>
      <div className="d-flex flex-wrap gap-2 mb-3" aria-label="Catégories du diagnostic">
        {Object.entries(RECONCILIATION_CATEGORIES).map(([key, def]) => <button key={key} type="button"
          className={`btn btn-sm ${category === key ? "btn-secondary" : "btn-outline-secondary"}`}
          aria-pressed={category === key} onClick={() => { setCategory(key); setPage(1); }}>
          {def.label} : {data?.counts?.[def.count] ?? "—"}
        </button>)}
      </div>
      <p>{RECONCILIATION_CATEGORIES[category].explanation}</p>
      {loading && <p role="status">Chargement du diagnostic…</p>}
      {error && <div className="alert alert-danger" role="alert">{error}</div>}
      {data && <>
        <p className="small text-muted">Vérifié le {formatDiagnosticDate(data.checkedAt)}.
          {" "}Page {data.page} · {data.items.length} dossier(s) affiché(s) sur {data.total}.</p>
        {data.items.length === 0 ? <p>Aucun dossier dans cette catégorie à la date de vérification.</p>
          : <div className="table-responsive"><table className="table table-sm">
            <thead><tr><th>Dossier</th><th>État</th><th>Résultat enregistré</th><th>Prévu (UTC)</th><th>Mis à jour (UTC)</th></tr></thead>
            <tbody>{data.items.map((item, index) => <tr key={item.id}>
              <td>{(data.page - 1) * data.limit + index + 1}</td>
              <td>{category === "held" ? "Sous garde" : category === "indeterminate" ? "Exécution à vérifier"
                : category === "reservations" ? "Réservation à vérifier" : "Suivi automatique historique"}</td>
              <td>{RESULT_CONFIG[resultCode(item.resultStatus)]?.label || "Non renseigné"}</td>
              <td>{formatDiagnosticDate(item.scheduledAt)}</td><td>{formatDiagnosticDate(item.updatedAt)}</td>
            </tr>)}</tbody>
          </table></div>}
        {data.paginationLimitReached && <p className="alert alert-warning" role="status">
          Limite de consultation atteinte. Le diagnostic affiché est partiel.</p>}
      </>}
      <div className="d-flex flex-wrap gap-2">
        <button type="button" className="btn btn-outline-secondary btn-sm" disabled={loading}
          onClick={() => setRefreshVersion((value) => value + 1)}>Actualiser le diagnostic</button>
        <button type="button" className="btn btn-outline-secondary btn-sm" disabled={loading || page === 1}
          onClick={() => setPage((value) => value - 1)}>Page précédente</button>
        <button type="button" className="btn btn-outline-secondary btn-sm" disabled={loading || !data?.hasMore || data?.paginationLimitReached}
          onClick={() => setPage((value) => value + 1)}>Page suivante</button>
      </div>
    </div>}
  </section>;
}
